import React, { useState, useEffect, useMemo } from 'react';
import exhibitionsConfig from '../exhibitionsConfig.json';
import SurveyButton from './SurveyButton';
import { Exhibition as ExhibitionType } from '../utils/feishuSurvey';

interface Exhibition extends ExhibitionType {
  month: number;
  quarter: number;
  year: number;
  scale: number;
  visitors: number;
  status: string;
  rawDatePeriod: string;
  rawScale: string;
  rawVisitors: string;
  rawVenue: string;
}

const ExhibitionsList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('all');

  const [selectedQuarter, setSelectedQuarter] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedCity, setSelectedCity] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('date');
  const [selectedExhibition, setSelectedExhibition] = useState<Exhibition | null>(null);

  const itemsPerPage = exhibitionsConfig.settings.pageSize;

  // 季度到月份的映射
  const quarterToMonths = {
    1: [1, 2, 3],  // Q1: 1-3月
    2: [4, 5, 6],  // Q2: 4-6月
    3: [7, 8, 9],  // Q3: 7-9月
    4: [10, 11, 12] // Q4: 10-12月
  };

  // 获取当前选中季度的可用月份
  const getAvailableMonths = () => {
    if (selectedQuarter === 'all') {
      return Array.from({ length: 12 }, (_, i) => i + 1);
    }
    return quarterToMonths[parseInt(selectedQuarter)] || [];
  };

  // 当季度变化时，重置月份选择
  const handleQuarterChange = (quarter: string) => {
    setSelectedQuarter(quarter);
    setSelectedMonth('all'); // 重置月份选择
  };

  // 当月份变化时，可能需要更新季度
  const handleMonthChange = (month: string) => {
    setSelectedMonth(month);
    
    // 如果选择了具体月份，自动更新对应的季度
    if (month !== 'all') {
      const monthNum = parseInt(month);
      if (monthNum >= 1 && monthNum <= 3) {
        setSelectedQuarter('1');
      } else if (monthNum >= 4 && monthNum <= 6) {
        setSelectedQuarter('2');
      } else if (monthNum >= 7 && monthNum <= 9) {
        setSelectedQuarter('3');
      } else if (monthNum >= 10 && monthNum <= 12) {
        setSelectedQuarter('4');
      }
    }
  };

  // 过滤和排序展会
  const filteredAndSortedExhibitions = useMemo(() => {
    let filtered = exhibitionsConfig.exhibitions.filter((exhibition: Exhibition) => {
      const searchLower = searchTerm.toLowerCase().trim();
      const matchesSearch = !searchLower || 
                           exhibition.name.toLowerCase().includes(searchLower) ||
                           exhibition.englishName.toLowerCase().includes(searchLower) ||
                           exhibition.city.toLowerCase().includes(searchLower) ||
                           exhibition.industry.toLowerCase().includes(searchLower);
      const matchesIndustry = selectedIndustry === 'all' || exhibition.industry === selectedIndustry;
      const matchesQuarter = selectedQuarter === 'all' || exhibition.quarter.toString() === selectedQuarter;
      const matchesMonth = selectedMonth === 'all' || exhibition.month.toString() === selectedMonth;
      const matchesStatus = selectedStatus === 'all' || exhibition.status === selectedStatus;
      const matchesCity = selectedCity === 'all' || exhibition.city === selectedCity;

      return matchesSearch && matchesIndustry && matchesQuarter && matchesMonth && matchesStatus && matchesCity;
    });

    // 排序
    filtered.sort((a: Exhibition, b: Exhibition) => {
      switch (sortBy) {
        case 'date':
          return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
        case 'name':
          return a.name.localeCompare(b.name);
        case 'visitors':
          return (Number(b.visitors) || 0) - (Number(a.visitors) || 0);
        case 'scale':
          return (Number(b.scale) || 0) - (Number(a.scale) || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [searchTerm, selectedIndustry, selectedQuarter, selectedMonth, selectedStatus, selectedCity, sortBy]);

  // 分页
  const totalPages = Math.ceil(filteredAndSortedExhibitions.length / itemsPerPage);
  const paginatedExhibitions = filteredAndSortedExhibitions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // 获取状态颜色
  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      '接受预定': 'bg-blue-100 text-blue-700',
      '火热招展': 'bg-red-100 text-red-700',
      '即将开始': 'bg-yellow-100 text-yellow-700',
      '已结束': 'bg-gray-100 text-gray-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  // 获取行业颜色
  const getIndustryColor = (industry: string) => {
    const colors: { [key: string]: string } = {
      '农业': 'bg-green-100 text-green-600',
      '塑料': 'bg-blue-100 text-blue-600',
      '包装': 'bg-orange-100 text-orange-600',
      '光学': 'bg-purple-100 text-purple-600',
      '建筑': 'bg-gray-100 text-gray-600',
      '食品': 'bg-red-100 text-red-600',
      '化工': 'bg-yellow-100 text-yellow-600',
      '汽车': 'bg-indigo-100 text-indigo-600',
      '电子': 'bg-cyan-100 text-cyan-600',
      '医疗': 'bg-pink-100 text-pink-600',
      '纺织': 'bg-amber-100 text-amber-600',
      '机械': 'bg-slate-100 text-slate-600',
      '能源': 'bg-lime-100 text-lime-600',
      '家具': 'bg-brown-100 text-brown-600',
      '旅游': 'bg-sky-100 text-sky-600',
      '教育': 'bg-violet-100 text-violet-600',
      '体育': 'bg-emerald-100 text-emerald-600',
      '其他': 'bg-gray-100 text-gray-600'
    };
    return colors[industry] || 'bg-gray-100 text-gray-600';
  };

  // 降级到联系页面的函数
  const fallbackToContact = () => {
    setSelectedExhibition(null);
    setTimeout(() => {
      const contactElement = document.getElementById('contact');
      if (contactElement) {
        contactElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 头部标题 */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">2026年俄罗斯展会计划</h1>

          </div>
        </div>
      </div>

      {/* 筛选和搜索区域 */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* 搜索框 - 独占一行 */}
          <div className="mb-4">
            <div className="relative">
              <input
                type="text"
                placeholder="搜索展会名称..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* 筛选项目 - 第二行 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
            {/* 季度筛选 */}
            <select
              value={selectedQuarter}
              onChange={(e) => handleQuarterChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">所有季度</option>
              {exhibitionsConfig.filters.quarters.map((quarter: number) => (
                <option key={quarter} value={quarter.toString()}>
                  第{quarter}季度
                </option>
              ))}
            </select>

            {/* 月份筛选 */}
            <select
              value={selectedMonth}
              onChange={(e) => handleMonthChange(e.target.value)}
              disabled={selectedQuarter !== 'all' && getAvailableMonths().length === 0}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="all">所有月份</option>
              {getAvailableMonths().map((month) => (
                <option key={month} value={month.toString()}>
                  {month}月
                </option>
              ))}
            </select>

            {/* 城市筛选 */}
            <select
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">所有城市</option>
              {exhibitionsConfig.filters.cities.map((city: string) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>

            {/* 行业筛选 */}
            <select
              value={selectedIndustry}
              onChange={(e) => setSelectedIndustry(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">所有行业</option>
              {exhibitionsConfig.filters.industries.map((industry: string) => (
                <option key={industry} value={industry}>
                  {industry}
                </option>
              ))}
            </select>
          </div>

          {/* 排序选项 */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              共找到 {filteredAndSortedExhibitions.length} 个展会
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="date">按时间排序</option>
              <option value="name">按名称排序</option>
              <option value="visitors">按观众人数排序</option>
              <option value="scale">按展会规模排序</option>
            </select>
          </div>
        </div>
      </div>

      {/* 展会列表 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {paginatedExhibitions.map((exhibition: Exhibition) => (
            <div
              key={exhibition.id}
              className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer border border-gray-200"
              onClick={() => setSelectedExhibition(exhibition)}
            >
              {/* 展会卡片头部 */}
              <div className="p-4 sm:p-6">
                <div className="flex items-start justify-between mb-3 sm:mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center mb-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-2 ${getIndustryColor(exhibition.industry)}`}>
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0h8v12H6V4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                        {exhibition.industry}
                      </span>
                    </div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 line-clamp-2">
                      {exhibition.name}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600 mb-3">
                      {exhibition.englishName}
                    </p>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${getStatusColor(exhibition.status)}`}>
                    {exhibition.status}
                  </div>
                </div>

                {/* 展会详情 */}
                <div className="space-y-1.5 sm:space-y-2">
                  <div className="flex items-center text-xs sm:text-sm text-gray-600">
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="truncate">{exhibition.rawDatePeriod}</span>
                  </div>
                  
                  <div className="flex items-center text-xs sm:text-sm text-gray-600">
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="truncate">{exhibition.city}</span>
                  </div>

                  <div className="flex items-center text-xs sm:text-sm text-gray-600">
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <span className="truncate">{exhibition.rawScale}</span>
                  </div>

                  <div className="flex items-center text-xs sm:text-sm text-gray-600">
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span className="truncate">{exhibition.rawVisitors}人次</span>
                  </div>
                </div>

                <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100">
                  <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                    查看详情 →
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 分页控件 */}
        {totalPages > 1 && (
          <div className="mt-8 flex justify-center">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                上一页
              </button>
              
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 text-sm border rounded-lg ${
                        currentPage === page
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 展会详情弹窗 */}
      {selectedExhibition && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-black bg-opacity-50" aria-hidden="true" onClick={() => setSelectedExhibition(null)}></div>

            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full w-full max-w-full border border-gray-200 z-10">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 max-h-[70vh] overflow-y-auto">
                <div className="sm:flex sm:items-start">
                  <div className="w-full">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">
                          {selectedExhibition.name}
                        </h3>
                        <p className="text-lg text-gray-600 mb-4">
                          {selectedExhibition.englishName}
                        </p>
                      </div>
                      <button
                        onClick={() => setSelectedExhibition(null)}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">展会信息</h4>
                        <dl className="space-y-2">
                          <div className="flex">
                            <dt className="font-medium text-gray-500 w-24">时间:</dt>
                            <dd className="text-gray-900">{selectedExhibition.rawDatePeriod}</dd>
                          </div>
                          <div className="flex">
                            <dt className="font-medium text-gray-500 w-24">城市:</dt>
                            <dd className="text-gray-900">{selectedExhibition.city}</dd>
                          </div>
                          <div className="flex">
                            <dt className="font-medium text-gray-500 w-24">场馆:</dt>
                            <dd className="text-gray-900">{selectedExhibition.venue}</dd>
                          </div>
                          <div className="flex">
                            <dt className="font-medium text-gray-500 w-24">行业:</dt>
                            <dd className="text-gray-900">{selectedExhibition.industry}</dd>
                          </div>
                        </dl>
                      </div>

                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">规模数据</h4>
                        <dl className="space-y-2">
                          <div className="flex">
                            <dt className="font-medium text-gray-500 w-24">规模:</dt>
                            <dd className="text-gray-900">{selectedExhibition.rawScale}</dd>
                          </div>
                          <div className="flex">
                            <dt className="font-medium text-gray-500 w-24">观众:</dt>
                            <dd className="text-gray-900">{selectedExhibition.rawVisitors}人次</dd>
                          </div>
                          <div className="flex">
                            <dt className="font-medium text-gray-500 w-24">状态:</dt>
                            <dd>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedExhibition.status)}`}>
                                {selectedExhibition.status}
                              </span>
                            </dd>
                          </div>
                        </dl>
                      </div>
                    </div>

                    {/* 飞书文档链接 */}
                    {selectedExhibition.feishuDoc && selectedExhibition.feishuDoc !== '#' && (
                      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                        <button
                          onClick={() => window.open(selectedExhibition.feishuDoc, '_blank')}
                          className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          查看招展函详情
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setSelectedExhibition(null)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  关闭
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // 尝试打开飞书咨询问卷
                    import('../utils/feishuSurvey').then(({ openConsultationSurvey, loadFeishuSurveyConfig }) => {
                      loadFeishuSurveyConfig()
                        .then(config => {
                          if (config && config.consultationUrl) {
                            // 打开飞书咨询问卷
                            openConsultationSurvey(selectedExhibition, config);
                            setSelectedExhibition(null);
                          } else {
                            // 配置未找到，降级到原有逻辑
                            console.warn('飞书咨询问卷配置未找到，降级到联系方式');
                            fallbackToContact();
                          }
                        })
                        .catch(error => {
                          console.error('加载飞书问卷配置失败:', error);
                          fallbackToContact();
                        });
                    });
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  咨询参展
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExhibitionsList;